/**
 * BudgetEnginePage — Budget-to-Cash Assumption Engine (Feature 4).
 * P&L summary grouped by category, revenue by LOB, and cash conversion rules
 * with timing profile visualisation.
 */

import React, { useMemo, useState } from 'react';
import { Card, CardContent } from '../../components/ui';
import { useBudgetEngine } from './api';
import { formatCurrency } from './utils';
import { cn } from '../../shared/lib/cn';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import type { BudgetFinancial, CashConversionRule } from './types';

const CATEGORY_COLORS: Record<string, string> = {
  Revenue: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  'Cost of Revenue': 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  'Operating Expenses': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  'Other Income': 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  'Other Expense': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
};

const LOB_COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4', '#ec4899'];

function varianceColor(v: number | undefined | null): string {
  if (v == null || v === 0) return '';
  return v > 0
    ? 'text-emerald-600 dark:text-emerald-400'
    : 'text-red-600 dark:text-red-400';
}

function parseTiming(raw: string): Record<string, number> | null {
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === 'object' && parsed !== null) return parsed as Record<string, number>;
  } catch {
    /* fall through */
  }
  return null;
}

function TimingBar({ timing }: { timing: Record<string, number> }) {
  const entries = Object.entries(timing);
  const total = entries.reduce((s, [, v]) => s + v, 0);
  if (total === 0) return <span className="text-xs text-gray-400">—</span>;

  return (
    <div className="flex items-center gap-1 w-full">
      {entries.map(([label, value], i) => {
        const pct = (value / total) * 100;
        if (pct === 0) return null;
        return (
          <div
            key={label}
            className="h-5 rounded text-[9px] font-medium flex items-center justify-center text-white overflow-hidden"
            style={{
              width: `${Math.max(pct, 8)}%`,
              backgroundColor: LOB_COLORS[i % LOB_COLORS.length],
            }}
            title={`${label}: ${value}%`}
          >
            {pct >= 15 ? `${label}` : ''}
          </div>
        );
      })}
    </div>
  );
}

export default function BudgetEnginePage() {
  const { data, isLoading } = useBudgetEngine();
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const grouped = useMemo(() => {
    if (!data?.financials?.length) return new Map<string, BudgetFinancial[]>();
    const m = new Map<string, BudgetFinancial[]>();
    for (const f of data.financials) {
      const cat = f.line_item_category || 'Other';
      if (!m.has(cat)) m.set(cat, []);
      m.get(cat)!.push(f);
    }
    return m;
  }, [data]);

  const revenueLOB = useMemo(() => {
    if (!data?.revenue?.length) return [];
    const byLOB = new Map<string, number>();
    for (const r of data.revenue) {
      const lob = r.line_of_business || 'Other';
      byLOB.set(lob, (byLOB.get(lob) ?? 0) + r.amount);
    }
    return Array.from(byLOB.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [data]);

  const toggleCategory = (cat: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6 mt-2">
        <Card>
          <CardContent className="p-8">
            <div className="animate-pulse space-y-4">
              <div className="h-6 w-48 bg-gray-200 dark:bg-dark-surface-2 rounded" />
              <div className="h-4 w-full bg-gray-200 dark:bg-dark-surface-2 rounded" />
              <div className="h-4 w-full bg-gray-200 dark:bg-dark-surface-2 rounded" />
              <div className="h-4 w-3/4 bg-gray-200 dark:bg-dark-surface-2 rounded" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data || !data.financials?.length) {
    return (
      <div className="space-y-6 mt-2">
        <Card>
          <CardContent className="p-8 text-center">
            <h2 className="text-lg font-semibold text-charcoal dark:text-gray-100 mb-2">
              Budget Engine
            </h2>
            <p className="text-gray-500 dark:text-gray-400">
              No budget data available. Load budget financials to enable the Budget-to-Cash engine.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalBudget = data.financials.reduce((s, f) => s + f.budget_amount, 0);
  const totalForecast = data.financials.reduce((s, f) => s + (f.forecast_amount ?? 0), 0);
  const totalActual = data.financials.reduce((s, f) => s + (f.actual_amount ?? 0), 0);
  const totalVariance = data.financials.reduce((s, f) => s + (f.variance_to_budget ?? 0), 0);

  return (
    <div className="space-y-6 mt-2">
      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Total Budget', value: formatCurrency(totalBudget, true) },
          { label: 'Total Forecast', value: formatCurrency(totalForecast, true) },
          { label: 'Total Actual', value: formatCurrency(totalActual, true) },
          { label: 'Total Variance', value: formatCurrency(totalVariance, true), accent: varianceColor(totalVariance) },
        ].map(kpi => (
          <Card key={kpi.label}>
            <CardContent className="p-3">
              <p className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
                {kpi.label}
              </p>
              <p className={cn('text-xl font-bold', kpi.accent || 'text-charcoal dark:text-gray-100')}>
                {kpi.value}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* P&L Summary Table */}
      <Card>
        <CardContent className="p-0">
          <div className="px-4 pt-4 pb-2">
            <h3 className="text-sm font-semibold text-charcoal dark:text-gray-100">
              P&L Summary — Budget vs Forecast vs Actual
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-surface-2">
                  <th className="text-left py-2 px-4 font-medium text-gray-500 dark:text-gray-400">Category / Line Item</th>
                  <th className="text-right py-2 px-3 font-medium text-gray-500 dark:text-gray-400">Budget</th>
                  <th className="text-right py-2 px-3 font-medium text-gray-500 dark:text-gray-400">Forecast</th>
                  <th className="text-right py-2 px-3 font-medium text-gray-500 dark:text-gray-400">Actual</th>
                  <th className="text-right py-2 px-3 font-medium text-gray-500 dark:text-gray-400">Variance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-dark-border/50">
                {Array.from(grouped.entries()).map(([category, items]) => {
                  const catBudget = items.reduce((s, i) => s + i.budget_amount, 0);
                  const catForecast = items.reduce((s, i) => s + (i.forecast_amount ?? 0), 0);
                  const catActual = items.reduce((s, i) => s + (i.actual_amount ?? 0), 0);
                  const catVariance = items.reduce((s, i) => s + (i.variance_to_budget ?? 0), 0);
                  const isExpanded = expandedCategories.has(category);

                  return (
                    <React.Fragment key={category}>
                      <tr
                        className="bg-gray-50/50 dark:bg-dark-surface-2/30 cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-surface-2/60"
                        onClick={() => toggleCategory(category)}
                      >
                        <td className="py-2 px-4 font-semibold text-charcoal dark:text-gray-100">
                          <span className="flex items-center gap-2">
                            <span className="text-xs text-gray-400">{isExpanded ? '▼' : '▶'}</span>
                            <span className={cn('px-2 py-0.5 rounded text-xs font-medium', CATEGORY_COLORS[category] || 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300')}>
                              {category}
                            </span>
                          </span>
                        </td>
                        <td className="py-2 px-3 text-right font-semibold tabular-nums text-charcoal dark:text-gray-100">
                          {formatCurrency(catBudget, true)}
                        </td>
                        <td className="py-2 px-3 text-right font-semibold tabular-nums text-charcoal dark:text-gray-100">
                          {formatCurrency(catForecast, true)}
                        </td>
                        <td className="py-2 px-3 text-right font-semibold tabular-nums text-charcoal dark:text-gray-100">
                          {formatCurrency(catActual, true)}
                        </td>
                        <td className={cn('py-2 px-3 text-right font-semibold tabular-nums', varianceColor(catVariance))}>
                          {formatCurrency(catVariance, true)}
                        </td>
                      </tr>
                      {isExpanded && items.map((item, idx) => (
                        <tr key={`${category}-${idx}`} className="hover:bg-gray-50 dark:hover:bg-dark-surface-2/50">
                          <td className="py-1.5 px-4 pl-12 text-gray-600 dark:text-gray-400 text-xs">
                            {item.line_item_name}
                          </td>
                          <td className="py-1.5 px-3 text-right tabular-nums text-gray-600 dark:text-gray-400 text-xs">
                            {formatCurrency(item.budget_amount, true)}
                          </td>
                          <td className="py-1.5 px-3 text-right tabular-nums text-gray-600 dark:text-gray-400 text-xs">
                            {item.forecast_amount != null ? formatCurrency(item.forecast_amount, true) : '—'}
                          </td>
                          <td className="py-1.5 px-3 text-right tabular-nums text-gray-600 dark:text-gray-400 text-xs">
                            {item.actual_amount != null ? formatCurrency(item.actual_amount, true) : '—'}
                          </td>
                          <td className={cn('py-1.5 px-3 text-right tabular-nums text-xs', varianceColor(item.variance_to_budget))}>
                            {item.variance_to_budget != null ? formatCurrency(item.variance_to_budget, true) : '—'}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Bottom row: Revenue by LOB + Conversion Rules */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Revenue by LOB */}
        <Card>
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-charcoal dark:text-gray-100 mb-4">
              Revenue by Line of Business
            </h3>
            {revenueLOB.length === 0 ? (
              <p className="text-sm text-gray-400">No revenue detail available.</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={revenueLOB} layout="vertical" margin={{ left: 10, right: 20, top: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis
                    type="number"
                    tickFormatter={v => formatCurrency(v, true)}
                    tick={{ fontSize: 11, fill: '#9ca3af' }}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={120}
                    tick={{ fontSize: 11, fill: '#9ca3af' }}
                  />
                  <Tooltip
                    formatter={(v: number) => formatCurrency(v)}
                    contentStyle={{
                      backgroundColor: 'var(--color-surface, #fff)',
                      border: '1px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                  />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {revenueLOB.map((_, i) => (
                      <Cell key={i} fill={LOB_COLORS[i % LOB_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Conversion Rules Panel */}
        <Card>
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-charcoal dark:text-gray-100 mb-4">
              Cash Conversion Rules
            </h3>
            {!data.conversion_rules?.length ? (
              <p className="text-sm text-gray-400">No conversion rules defined.</p>
            ) : (
              <div className="space-y-3">
                {data.conversion_rules.map((rule: CashConversionRule) => {
                  const timing = parseTiming(rule.timing_profile);
                  return (
                    <div
                      key={rule.id}
                      className="border border-gray-100 dark:border-dark-border rounded-lg p-3"
                    >
                      <div className="flex items-start justify-between mb-1.5">
                        <div>
                          <p className="text-sm font-medium text-charcoal dark:text-gray-100">
                            {rule.rule_name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {rule.line_item_category} · {rule.conversion_type}
                          </p>
                        </div>
                        {rule.confidence_level && (
                          <span className={cn(
                            'px-2 py-0.5 rounded text-[10px] font-medium',
                            rule.confidence_level === 'high'
                              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                              : rule.confidence_level === 'medium'
                                ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                                : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
                          )}>
                            {rule.confidence_level}
                          </span>
                        )}
                      </div>
                      {timing ? (
                        <TimingBar timing={timing} />
                      ) : (
                        <p className="text-xs text-gray-400 italic">{rule.timing_profile}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
